import { HelpCircle, Phone } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQSection = () => {
  const faqs = [
    {
      question: "Combien de temps prend une réparation d'écran ?",
      answer: "En général, une réparation d'écran de smartphone prend entre 30 minutes et 1 heure. Pour les réparations express, nous pouvons intervenir en 30 minutes. Nous vous donnons toujours une estimation précise du temps avant de commencer.",
    },
    {
      question: "Proposez-vous une garantie sur les réparations ?",
      answer: "Oui, toutes nos réparations sont garanties 6 mois. Cette garantie couvre les pièces remplacées et la main d'œuvre. Si un problème survient suite à notre intervention, nous le prenons en charge gratuitement.",
    },
    {
      question: "Quelles marques de téléphone réparez-vous ?",
      answer: "Toutes les marques : iPhone (Apple), Samsung, Huawei, Xiaomi, Honor, Google Pixel, OnePlus, Oppo et bien d'autres. Nous réparons aussi iPad, MacBook, Apple Watch, et les consoles PlayStation, Nintendo Switch et Xbox. Notre équipe est formée sur tous les modèles récents et anciens.",
    },
    {
      question: "Dois-je prendre rendez-vous pour une réparation ?",
      answer: "Non, vous pouvez venir directement sans rendez-vous dans notre boutique à Montpellier. Pour les réparations express ou les interventions complexes, nous vous conseillons de nous appeler au 09 83 82 07 54 pour vérifier la disponibilité des pièces.",
    },
    {
      question: "Utilisez-vous des pièces d'origine ?",
      answer: "Nous utilisons des pièces de qualité A+, testées et certifiées. Pour certains modèles, nous proposons aussi des pièces d'origine constructeur. Nous vous informons toujours de la qualité des pièces avant la réparation.",
    },
    {
      question: "Combien coûte la réparation d'un écran iPhone ?",
      answer: "Le prix varie selon le modèle. Pour un iPhone récent (13, 14, 15), comptez entre 79€ et 199€ selon la génération. Nous affichons tous nos tarifs de manière transparente et le devis est gratuit.",
    },
    {
      question: "Réparez-vous les MacBook et ordinateurs portables ?",
      answer: "Oui, nous sommes spécialisés dans la réparation de MacBook (Pro, Air) et PC portables de toutes marques. Nous intervenons sur les écrans, batteries, claviers, cartes mères et composants internes.",
    },
    {
      question: "Que faire si mon téléphone est tombé dans l'eau ?",
      answer: "Éteignez-le immédiatement et ne tentez pas de le recharger. Apportez-le nous rapidement pour un diagnostic. Nous effectuons un nettoyage ultrasonique et remplaçons les composants endommagés. Plus vite vous agissez, meilleures sont les chances de récupération.",
    },
    {
      question: "Où êtes-vous situés à Montpellier ?",
      answer: "Notre boutique se trouve au 7 place des martyrs de la résistance, 34000 Montpellier, en plein centre-ville. Facilement accessible en tramway et avec des parkings à proximité.",
    },
    {
      question: "Proposez-vous des réparations à domicile ?",
      answer: "Pour le moment, nous n'effectuons pas de réparations à domicile. Cependant, notre boutique est facilement accessible et nous proposons un service express pour minimiser votre temps d'attente.",
    },
  ];

  const half = Math.ceil(faqs.length / 2);
  const colA = faqs.slice(0, half);
  const colB = faqs.slice(half);

  const renderItem = (faq, idx) => (
    <AccordionItem
      key={idx}
      value={`item-${idx}`}
      className="group review-glass-card !rounded-xl border-0 px-4 overflow-hidden data-[state=open]:ring-1 data-[state=open]:ring-purple-300/60 transition-all duration-300"
      data-testid={`faq-item-${idx}`}
    >
      <AccordionTrigger
        className="text-left font-medium text-slate-800 hover:text-purple-600 py-3.5 text-[14px] [&[data-state=open]]:text-purple-700 transition-colors gap-3"
        data-testid={`faq-question-${idx}`}
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="flex-shrink-0 w-6 h-6 rounded-md bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-purple-600 text-[11px] font-bold group-data-[state=open]:from-purple-500 group-data-[state=open]:to-pink-500 group-data-[state=open]:text-white transition-all">
            {idx + 1}
          </span>
          <span className="leading-snug">{faq.question}</span>
        </span>
      </AccordionTrigger>
      <AccordionContent
        className="text-slate-600 pb-4 leading-relaxed pl-9 text-[13.5px]"
        data-testid={`faq-answer-${idx}`}
      >
        {faq.answer}
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <section
      id="faq"
      className="relative py-16 sm:py-20 overflow-hidden"
      data-testid="faq-section"
    >
      {/* Background */}
      <div className="absolute inset-0 gradient-section-purple" />

      {/* Decorative blobs (provide color through the glass cards) */}
      <div className="absolute top-1/3 right-[5%] w-72 h-72 bg-gradient-to-br from-purple-300/30 to-pink-300/25 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-[5%] w-80 h-80 bg-gradient-to-br from-blue-300/25 to-cyan-300/20 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        {/* Section Header — compact */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full mb-4">
            <HelpCircle size={14} className="text-purple-500" />
            <span className="text-purple-600 font-semibold text-xs uppercase tracking-wider" data-testid="faq-label">
              Questions fréquentes
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="faq-title">
            Tout ce que vous devez{' '}
            <span className="gradient-text">savoir</span>
          </h2>
          <p className="text-base text-slate-600" data-testid="faq-description">
            Toutes les réponses aux questions courantes sur nos réparations à Montpellier.
          </p>
        </div>

        {/* FAQ Accordion — 2 columns on desktop */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-3" data-testid="faq-accordion">
          <Accordion type="multiple" className="space-y-3">
            {colA.map((faq, idx) => renderItem(faq, idx))}
          </Accordion>
          <Accordion type="multiple" className="space-y-3">
            {colB.map((faq, idx) => renderItem(faq, idx + half))}
          </Accordion>
        </div>

        {/* Inline CTA — minimaliste */}
        <p className="text-center mt-10 text-sm text-slate-600">
          Une autre question&nbsp;?{' '}
          <a
            href="tel:+33983820754"
            className="inline-flex items-center gap-1.5 font-semibold text-purple-600 hover:text-purple-700 underline underline-offset-2 transition-colors"
            data-testid="faq-call-cta"
          >
            <Phone size={13} strokeWidth={2.4} />
            Appelez-nous au 09 83 82 07 54
          </a>
        </p>
      </div>
    </section>
  );
};

export default FAQSection;
